// Outstanding / unanswered question helpers shared by the admin vendor-detail
// tabs and the vendor post-submit view. completionByQuestion (from
// computeProgress) only contains visible questions; a non-null CompletionReason
// means incomplete — the exact signal that drives the X/Y progress bar.

import type { CompletionReason } from './visibility.ts';
import { frozenApprovedDoc } from './frozenApprovedDoc.ts';
import { hasPublishedRework } from './reviewState.ts';

type CountableQuestion = {
  id: string;
  required: boolean;
  exclude_from_compliance: boolean;
  // Compliance-doc flag: a frozen renewal row (see below) is only possible on
  // a compliance doc. Optional so non-admin callers (postSubmitLocked) need
  // not supply it.
  include_in_compliance_documents?: boolean;
  // Retired (soft-deleted). A frozen vendor still receives the row, with the
  // live deleted_at overlaid, but it never blocks submit.
  deleted_at?: string | null;
};

type CountableSubmission = {
  status: string;
  // 'admin' covers admins and reviewers (both post comments as 'admin'). When
  // they have the last word the item is awaiting the vendor, so it is not an
  // outstanding admin action; null (no thread) or 'vendor' means the admin
  // still owns it.
  latest_comment_author?: 'admin' | 'vendor' | null;
  // A resubmit newer than the last review is the vendor acting even without a
  // comment — it keeps the item counted. See hasPublishedRework (reviewState).
  resubmitted_at?: string | null;
  reviewed_at?: string | null;
  // Present (admin endpoint) when this compliance doc's approved version was
  // later replaced: the questionnaire row is frozen on the original and its
  // renewal is reviewed in the Compliance Documents tab, so it is inert here.
  approved_files?: unknown[] | null;
  // Renewal re-verification marker — part of the freeze condition (a frozen row
  // is one whose renewal is pending reverify or re-approved).
  reverify_requested_at?: string | null;
};

// A required, visible question the vendor still owes an answer for.
// exclude_from_compliance questions are not counted (they sit outside the
// progress percentage), matching the progress bar.
export function isUnansweredRequired(
  question: CountableQuestion,
  completionByQuestion: Map<string, CompletionReason | null>,
): boolean {
  return (
    question.required &&
    !question.exclude_from_compliance &&
    completionByQuestion.get(question.id) != null
  );
}

// A required, visible question the vendor must still complete before submit
// will accept the questionnaire — the submit gate's own test, which lists every
// required incomplete question whatever its exclude flag, and skips retired
// ones (computeProgress's `missing`). Unlike isUnansweredRequired this does NOT
// skip exempt questions: exempt keeps a question out of the percentage and out
// of the admin's review work, never out of what the vendor owes (e.g. the
// declaration signature).
export function isOwedByVendor(
  question: CountableQuestion,
  completionByQuestion: Map<string, CompletionReason | null>,
): boolean {
  return (
    question.required && !question.deleted_at && completionByQuestion.get(question.id) != null
  );
}

// Items still needing attention in a tab: submissions awaiting a decision
// (pending/rejected, unless the ball is with the vendor — see below) PLUS
// required, visible, incomplete questions the vendor hasn't answered. Counted
// once per question — a pending/rejected sub wins and skips the incomplete check.
export function outstandingActionCount(
  questions: CountableQuestion[],
  completionByQuestion: Map<string, CompletionReason | null>,
  subByQ: Map<string, CountableSubmission>,
  // skipFrozen: for QUESTIONNAIRE-tab badges, a frozen renewal row is inert
  // (its action lives in the Compliance Documents tab). The Compliance
  // Documents tab badge itself must NOT skip it — that is exactly where the
  // renewal awaits review — so it calls with the default (false).
  opts?: { skipFrozen?: boolean },
): number {
  const skipFrozen = opts?.skipFrozen ?? false;
  let n = 0;
  for (const q of questions) {
    // A question marked "not part of compliance" is never review work, answered
    // or not. This guard has to lead: it used to live inside
    // isUnansweredRequired at the BOTTOM of the loop, which the pending/rejected
    // branch below never reached, so an exempt question started inflating the
    // badge the moment the vendor answered it — while the row itself said "no
    // review needed". Matches reviewProgress, which always skipped them.
    if (q.exclude_from_compliance) continue;
    const sub = subByQ.get(q.id);
    if (skipFrozen && frozenApprovedDoc(sub, q.include_in_compliance_documents ?? false).frozen) {
      continue;
    }
    if (sub?.status === 'pending' || sub?.status === 'rejected') {
      // Count while the admin still owns the item: a fresh submission awaiting
      // review, a vendor reply, or a vendor resubmit. Exclude only when the
      // admin/reviewer replied last AND the vendor hasn't acted since (no
      // resubmit newer than the last review) — then the ball is with the
      // vendor. Same rule as the per-row "Reply needed" signal in
      // admin/awaitingReply.ts and the isAwaitingVendor turn predicate — all
      // three now share hasPublishedRework, because hand-copying it is what let
      // PR #64 update this counter and miss the vendor's own badges.
      const adminRepliedLast = sub.latest_comment_author === 'admin';
      if (!adminRepliedLast || hasPublishedRework(sub)) n += 1;
      continue;
    }
    if (isUnansweredRequired(q, completionByQuestion)) n += 1;
  }
  return n;
}

// Whether a question's inputs should be read-only in the vendor post-submit
// view. Editable when the admin sent this answer back (status 'rejected') OR it
// is a stranded required+visible+unanswered question (exempt or not — the
// "Needs action" list flags both, via isOwedByVendor); locked otherwise.
//
// The gate is PER-ANSWER. It used to also require the company to be in
// 'changes', and that coupling only ever produced bugs, because a rejected
// answer routinely coexists with some other company status:
//   - 'changes' needs `hasAwaitingVendor && !hasAwaitingAdmin`, so a vendor with
//     one sent-back answer and one still awaiting review stays in 'review' —
//     and could not edit the answer the admin had just asked them to fix;
//   - an exempt question pinned hasAwaitingAdmin true, which blocked 'changes'
//     outright and read-only'd every rejection (fixed in the rollup, but only
//     after it shipped);
//   - the company now leaves 'changes' for 'review' as soon as the vendor
//     replies to every sent-back question, which is what lights the admin's
//     "Action required" — and must not take the answer read-only on its way.
// `companyStatus` is kept in the signature: callers pass it, and dropping a
// parameter to a widely-called predicate is a bigger change than this needs.
export function postSubmitLocked(
  question: CountableQuestion,
  _companyStatus: string | undefined,
  submission: { status: string } | undefined,
  completionByQuestion: Map<string, CompletionReason | null>,
): boolean {
  const editable =
    submission?.status === 'rejected' || isOwedByVendor(question, completionByQuestion);
  return !editable;
}
